const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

class StickyBuyButtons extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #mediaQueryList = null;

  /** @type {HTMLElement | null} */
  #sentinel = null;

  /** @type {HTMLElement | null} */
  #bar = null;

  /** @type {HTMLElement | null} */
  #spacer = null;

  connectedCallback() {
    this.#mediaQueryList = window.matchMedia(MOBILE_MEDIA_QUERY);

    // Safari < 14 uses addListener/removeListener
    if (this.#mediaQueryList.addEventListener) {
      this.#mediaQueryList.addEventListener('change', this.#onMediaChange);
    } else {
      this.#mediaQueryList.addListener(this.#onMediaChange);
    }

    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();

    if (!this.#mediaQueryList) return;

    if (this.#mediaQueryList.removeEventListener) {
      this.#mediaQueryList.removeEventListener('change', this.#onMediaChange);
    } else {
      this.#mediaQueryList.removeListener(this.#onMediaChange);
    }

    this.#mediaQueryList = null;
  }

  #onMediaChange = () => {
    this.#teardown();
    this.#setup();
  };

  #setup() {
    if (!this.#mediaQueryList?.matches) {
      this.#setStickyActive(false);
      return;
    }

    this.#sentinel = this.querySelector('[data-sticky-buy-buttons-sentinel]');
    this.#bar = this.querySelector('[data-sticky-buy-buttons-bar]');
    this.#spacer = this.querySelector('[data-sticky-buy-buttons-spacer]');

    if (!this.#sentinel || !this.#bar || !this.#spacer) return;

    // default state
    this.dataset.stickyState = this.dataset.stickyState || 'inactive';

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#setStickyActive(!entry.isIntersecting);
      },
      {
        threshold: 0,
      }
    );

    this.#intersectionObserver.observe(this.#sentinel);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.dataset.stickyState !== 'active') return;
        this.#syncHeights();
      });

      this.#resizeObserver.observe(this.#bar);
    }

    this.#syncHeights();
  }

  #teardown() {
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.#setStickyActive(false);

    this.#sentinel = null;
    this.#bar = null;
    this.#spacer = null;
  }

  #syncHeights() {
    if (!this.#bar || !this.#spacer) return;

    const height = this.#bar.getBoundingClientRect().height;

    this.#spacer.style.height = `${height}px`;
    document.documentElement.style.setProperty('--sticky-buy-buttons-height', `${height}px`);
  }

  /**
   * @param {boolean} active
   */
  #setStickyActive(active) {
    this.dataset.stickyState = active ? 'active' : 'inactive';

    if (!this.#spacer) return;

    if (active) {
      this.#syncHeights();
      this.#spacer.hidden = false;
      document.body.classList.add('has-sticky-buy-buttons');
    } else {
      this.#spacer.hidden = true;
      this.#spacer.style.height = '0px';
      document.body.classList.remove('has-sticky-buy-buttons');
      document.documentElement.style.removeProperty('--sticky-buy-buttons-height');
    }
  }
}

if (!customElements.get('sticky-buy-buttons')) {
  customElements.define('sticky-buy-buttons', StickyBuyButtons);
}



